import React, { useState, useEffect } from 'react';
import { Table, Input, Button, Tag, Space, Typography, Modal, Upload, Tabs, message, List, Checkbox } from 'antd';
import { SearchOutlined, PlusOutlined, UploadOutlined, DownloadOutlined, FileTextOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { UploadProps } from 'antd';
import datasetsData from '../../mock/datasets.json';
import cardsData from '../../mock/cards.json';

const { Title, Text } = Typography;
const { Search } = Input;
const { TabPane } = Tabs;
const { Dragger } = Upload;

interface DatasetData {
  id: string;
  name: string;
  type: string;
  cardCount: number;
  createTime: string;
  updateTime: string;
}

interface CardData {
  id: string;
  name: string;
  screenshot: string;
  dsl: any;
  status: string;
  datasets: string[];
  createTime: string;
  updateTime: string;
}

const DatasetManagement: React.FC = () => {
  const [datasets, setDatasets] = useState<DatasetData[]>([]);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [instructionText, setInstructionText] = useState('');
  const [currentExportDataset, setCurrentExportDataset] = useState<DatasetData | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // 从模拟数据加载数据集
    setDatasets(datasetsData.datasets);
  }, []);

  // 处理搜索
  const handleSearch = (value: string) => {
    setSearchText(value);
    if (!value) {
      setDatasets(datasetsData.datasets);
      return;
    }
    
    const filteredDatasets = datasetsData.datasets.filter(dataset => 
      dataset.name.toLowerCase().includes(value.toLowerCase())
    );
    
    setDatasets(filteredDatasets);
  };

  // 跳转到创建数据集页面
  const navigateToCreate = () => {
    navigate('/dataset-management/create');
  };

  // 处理分页变化
  const handleTableChange = (pagination: any) => {
    setPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  // 处理添加数据
  const handleAddData = (record: DatasetData) => {
    navigate(`/dataset-management/add-data/${record.id}`);
  };

  // 处理导出数据集
  const handleExportDataset = (record: DatasetData) => {
    // 打开导出模态框
    setCurrentExportDataset(record);
    setExportModalVisible(true);
  };

  // 确认导出
  const handleExportConfirm = () => {
    if (currentExportDataset) {
      // 在实际应用中，这里应该调用API导出数据集
      // 这里我们只是模拟导出成功，并将指令信息拼接在导出文件中
      message.success(`${currentExportDataset.name} 数据集导出成功`);
      setExportModalVisible(false);
      setInstructionText('');
      setCurrentExportDataset(null);
    }
  };

  // 取消导出
  const handleExportCancel = () => {
    setExportModalVisible(false);
    setInstructionText('');
    setCurrentExportDataset(null);
  };

  // 查看数据集详情
  const handleViewDatasetDetail = (record: DatasetData) => {
    navigate(`/dataset-management/detail/${record.id}`);
  };

  // 表格列定义
  const columns = [
    {
      title: '序号',
      key: 'index',
      width: 80,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: '数据集名称',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: '卡片数量',
      dataIndex: 'cardCount',
      key: 'cardCount',
      width: 120,
      render: (count: number, record: DatasetData) => (
        <Button type="link" onClick={() => handleViewDatasetDetail(record)}>
          {count}
        </Button>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 180,
    },
    {
      title: '最后更新时间',
      dataIndex: 'updateTime',
      key: 'updateTime',
      width: 180,
    },
    {
      title: '操作',
      key: 'action',
      width: 240, // 增加宽度以容纳新按钮
      render: (_: any, record: DatasetData) => (
        <Space size="middle">
          <Button 
            type="primary" 
            icon={<FileTextOutlined />}
            onClick={() => handleAddData(record)}
          >
            添加数据
          </Button>
          <Button 
            type="primary" 
            icon={<FileTextOutlined />}
            onClick={() => handleViewDatasetDetail(record)}
          >
            详情
          </Button>
          <Button 
            type="primary" 
            icon={<DownloadOutlined />}
            onClick={() => handleExportDataset(record)}
          >
            导出
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Title level={4}>标注数据集</Title>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
          <Search
            placeholder="搜索数据集名称"
            allowClear
            enterButton={<SearchOutlined />}
            onSearch={handleSearch}
            style={{ width: 300 }}
          />
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={navigateToCreate}
          >
            创建数据集
          </Button>
        </div>
      </div>
      
      <Table
        columns={columns}
        dataSource={datasets}
        rowKey="id"
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
          pageSizeOptions: ['10', '20', '50', '100', '200', '500']
        }}
        onChange={handleTableChange}
        scroll={{ x: 1100 }}
      />

      {/* 导出数据集模态框 */}
      <Modal
        title="导出数据集"
        open={exportModalVisible}
        onOk={handleExportConfirm}
        onCancel={handleExportCancel}
        okText="确定"
        cancelText="取消"
      >
        {/* {currentExportDataset && (
          <div style={{ marginBottom: 16 }}>
            <Text strong>数据集名称: </Text>
            <Text>{currentExportDataset.name}</Text>
          </div>
        )} */}
        <div>
          <Text>请输入指令信息（将自动拼接在导出文件中）</Text>
          <Input.TextArea
            value={instructionText}
            onChange={(e) => setInstructionText(e.target.value)}
            placeholder="输入指令信息..."
            rows={4}
            style={{ marginTop: 8 }}
          />
        </div>
      </Modal>
    </div>
  );
};

export default DatasetManagement;